import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface InlineEditableTextProps {
  value: string;
  onSave: (next: string) => Promise<void> | void;
  className?: string;
  placeholder?: string;
  maxLength?: number;
}

export function InlineEditableText({ value, onSave, className, placeholder, maxLength = 300 }: InlineEditableTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = async () => {
    if (draft.trim() !== value.trim()) {
      await onSave(draft.trim());
    }
    setEditing(false);
  };

  const isMultiline = value.length > 60 || value.includes('\n');

  return (
    <div className={cn("w-full", className)}>
      {editing ? (
        isMultiline ? (
          <Textarea
            ref={inputRef as any}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) commit();
              if (e.key === "Escape") { setDraft(value); setEditing(false); }
            }}
            className="min-h-[60px] resize-none"
          />
        ) : (
          <Input
            ref={inputRef}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") { setDraft(value); setEditing(false); }
            }}
            className="h-8"
          />
        )
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="block w-full text-left hover:underline leading-snug break-words"
          title={value}
        >
          <span className="line-clamp-3">
            {value || <span className="text-muted-foreground">{placeholder || "Edit"}</span>}
          </span>
        </button>
      )}
    </div>
  );
}
